import { CommonModule } from '@angular/common';
import { Component, DestroyRef, ElementRef, inject, AfterViewInit, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { CX_ABSTRACT_COMPONENT_DATA } from '@b2b/components/cms/cx-abstract/cx-abstract.tokens';
import { ProductCardComponent } from '@b2b/components/product/product-card/product-card.component';
import { DEVICE_SIZE, mediaConfig } from '@app/core/models/deviceSize.model';
import { getB2BUnitSuccess } from '@b2b/features/b2b-unit/core/store/b2b-unit.actions';
import { ProductSearchService } from '@b2b/features/product/core/services/product-lookup.service';
import { Product } from '@b2b/features/product/core/utilities/product.model';
import { Actions, ofType } from '@ngrx/effects';
import { CarouselModule } from 'primeng/carousel';
import { take } from 'rxjs';

@Component({
  selector: 'cx-product-carousel',
  templateUrl: './cx-product-carousel.component.html',
  styleUrl: './cx-product-carousel.component.scss',
  imports: [ProductCardComponent, CommonModule, CarouselModule],
})
export class CxProductCarouselComponent implements AfterViewInit, OnDestroy, OnInit {
  protected readonly productSearchService = inject(ProductSearchService);
  protected readonly router = inject(Router);
  private readonly elementRef = inject(ElementRef);
  private readonly actions$ = inject(Actions);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly productCarousel = inject<{ title: string; products: string[] }>(CX_ABSTRACT_COMPONENT_DATA);

  protected responsiveOptions = [
    {
      breakpoint: mediaConfig.mediaFormats![DEVICE_SIZE.WIDESCREEN].width?.toString() + 'px',
      numVisible: 4,
      numScroll: 4,
    },
    {
      breakpoint: mediaConfig.mediaFormats![DEVICE_SIZE.DESKTOP].width?.toString() + 'px',
      numVisible: 3,
      numScroll: 3,
    },
    {
      breakpoint: mediaConfig.mediaFormats![DEVICE_SIZE.TABLET].width?.toString() + 'px',
      numVisible: 2,
      numScroll: 2,
    },
    {
      breakpoint: mediaConfig.mediaFormats![DEVICE_SIZE.MOBILE].width?.toString() + 'px',
      numVisible: 1,
      numScroll: 1,
    },
  ];

  private mutationObserver?: MutationObserver;

  products: Product[] = [];

  constructor() {
    this.loadProducts();
  }

  ngOnInit(): void {
    // Recarrega os produtos sempre que um cliente for selecionado/confirmado no modal
    this.actions$.pipe(ofType(getB2BUnitSuccess), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.products = [];
      this.productSearchService.clearCache();
      this.loadProducts();
    });
  }

  ngAfterViewInit(): void {
    this.removeAriaHidden();
  }

  ngOnDestroy(): void {
    this.mutationObserver?.disconnect();
  }

  private removeAriaHidden(): void {
    const carousel = this.elementRef.nativeElement.querySelector('.p-carousel');

    if (!carousel) return;

    const updateAriaHidden = () => {
      const items = carousel.querySelectorAll('.p-carousel-item[aria-hidden="true"]');
      items.forEach((item: Element) => {
        item.removeAttribute('aria-hidden');
      });
    };

    setTimeout(() => updateAriaHidden(), 0);

    this.mutationObserver = new MutationObserver(() => {
      updateAriaHidden();
    });

    this.mutationObserver.observe(carousel, {
      attributes: true,
      attributeFilter: ['aria-hidden'],
      subtree: true,
    });
  }

  protected loadProducts(): void {
    if (!this.productCarousel?.products) {
      return;
    }

    this.productSearchService.fetchedSearchProducts.set({});
    this.productSearchService.isLoadingSearchIds = {};

    this.productSearchService
      .search(this.productCarousel.products)
      .pipe(take(1))
      .subscribe(products => (this.products = products));
  }
}
